'use client';

import { useMemo } from 'react';

import { CHILD_OFFSET } from '../context/PopupStackContext';

/**
 * Hook for child panels that need to position relative to their parent popup's z-index.
 *
 * This is a simpler alternative to the full popup stack context when you already
 * have the parent's z-index and just need to calculate a child offset.
 *
 * @param parentZIndex - The z-index of the parent popup
 * @param position - Whether this child should be behind or in front of the parent
 * @returns The z-index to use for this child element
 *
 * @example
 * ```tsx
 * function SubPanel({ parentZIndex }) {
 *   const zIndex = useChildZIndex(parentZIndex, 'behind');
 *
 *   return (
 *     <Portal>
 *       <div style={{ zIndex }}>
 *         <SubPanelContent />
 *       </div>
 *     </Portal>
 *   );
 * }
 * ```
 */
export function useChildZIndex(parentZIndex: number, position: 'behind' | 'front'): number {
  return useMemo(() => {
    const offset = position === 'behind' ? -CHILD_OFFSET : CHILD_OFFSET;
    return parentZIndex + offset;
  }, [parentZIndex, position]);
}

export default useChildZIndex;
